import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import JSZip from "jszip";
import CloseIcon from "@mui/icons-material/Close";
import { Box, Typography, Grid, Paper, Tooltip, IconButton, Button, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { getDateRangeFromIsoStrings } from "../airspaceCharacterization/airspaceCharacterizationUtil";

export default function HimsAnalysisReport({ selectedReport, setSelectedReport }) {
    const [system, setSystem] = useState(null);
    const [components, setComponents] = useState([]);

    useEffect(() => {
        if (!selectedReport || !selectedReport.report_file) {
            return;
        }

        const loadZipFile = async () => {
            const zip = await JSZip.loadAsync(selectedReport.report_file.data);
            const system = {};
            const groups = {};
            for (const file of Object.keys(zip.files)) {
                // We only want groups of report.json, state_plot.png, and optionally fault_plot.png.
                const match = file.match(/^(.*)_(component_report|state_plot|fault_plot|system_report|status_plot)/);
                if (!match) {
                    continue;
                }

                const key = match[1].replace(" ", "_");
                groups[key] = groups[key] || {};
                if (file.endsWith("_component_report.json")) groups[key].report = file;
                if (file.endsWith("_state_plot.png")) groups[key].state = file;
                if (file.endsWith("_fault_plot.png")) groups[key].fault = file;
                if (file.endsWith("_system_report.json")) system.report = JSON.parse(await zip.file(file).async("string"));
                if (file.endsWith("_status_plot.png")) system.statusImg = `data:image/png;base64,${await zip.file(file).async("base64")}`;
            }

            const components = [];
            for (const key of Object.keys(groups).sort()) {
                const group = groups[key];

                // Each group needs to contain at least a report.json and state_plot.png.
                if (!group.report || !group.state) {
                    continue;
                }

                const reportJson = JSON.parse(await zip.file(group.report).async("string"));
                const stateSrc = await zip.file(group.state).async("base64");
                const faultSrc = group.fault ? await zip.file(group.fault).async("base64") : null;

                components.push({
                    uuid: reportJson.component_uuid,
                    name: reportJson.component_name,
                    stateImg: `data:image/png;base64,${stateSrc}`,
                    faultImg: faultSrc ? `data:image/png;base64,${faultSrc}` : null
                });
            }

            setSystem(system);
            setComponents(components);
        };
        loadZipFile();
    }, [selectedReport]);

    return (
        <Paper sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2, m: 2, maxWidth: "1500px" }}>
            <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
                <Typography variant="h6" sx={{ mr: "auto" }}>
                    HIMS Report
                    <Typography color={"text.secondary"}>{dayjs(selectedReport.date_created).format("MMMM D, YYYY [at] h:mm a")}</Typography>
                </Typography>
                <Tooltip title="Close">
                    <IconButton onClick={() => setSelectedReport(null)}>
                        <CloseIcon />
                    </IconButton>
                </Tooltip>
            </Box>
            {system && (
                <>
                    <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" } }}>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, maxWidth: "635px" }}>
                            <Typography variant="body1">Submission Criteria</Typography>
                            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1.7fr" }, gap: 1 }}>
                                {[
                                    { title: "System Name", value: system.report.system_name },
                                    { title: "Date Range", value: getDateRangeFromIsoStrings(system.report.time_start, system.report.time_end) },
                                    { title: "Mean Time Between Failures", value: system.report.mean_time_between_failure_hours + " hours" },
                                    { title: "System Status Changes", value: system.report.system_status_changes },
                                    {
                                        title: "Nominal Ratio",
                                        value: system.report.system_status_overview.nominal,
                                        tooltip: "The ratio of time spent in the Nominal Status with respect to the total analysis period."
                                    },
                                    {
                                        title: "Degraded Ratio",
                                        value: system.report.system_status_overview.degraded,
                                        tooltip: "The ratio of time spent in the Degraded Status with respect to the total analysis period."
                                    },
                                    {
                                        title: "Degraded Outage Ratio",
                                        value: system.report.system_status_overview.degraded_outage,
                                        tooltip: "The ratio of time spent in the Degraded Outage Status with respect to the total analysis period."
                                    },
                                    {
                                        title: "Failure Ratio",
                                        value: system.report.system_status_overview.failure,
                                        tooltip: "The ratio of time spent in the Failure Status with respect to the total analysis period."
                                    }
                                ].map(({ title, value, tooltip }, i) => (
                                    <Grid key={i} size={{ xs: 12, md: 6 }}>
                                        {tooltip ? (
                                            <Tooltip key={i} title={tooltip}>
                                                <Typography variant="body2" color={"text.secondary"} sx={{ display: "inline-block" }}>
                                                    {title}
                                                </Typography>
                                            </Tooltip>
                                        ) : (
                                            <Typography variant="body2" color={"text.secondary"}>
                                                {title}
                                            </Typography>
                                        )}
                                        <Typography variant="body1">{value}</Typography>
                                    </Grid>
                                ))}
                            </Box>
                        </Box>
                        <Box sx={{ minWidth: 0, mt: { md: "-40px" } }}>
                            <Typography variant="h6">System Status</Typography>
                            <HimsAnalysisImage name={system.report.system_name} img={system.statusImg} size={{ xs: 330, md: 300 }} />
                        </Box>
                    </Box>
                    {components.map((component) => (
                        <Box key={component.uuid}>
                            <Typography variant="h6">{component.name}</Typography>
                            <Grid container spacing={1}>
                                <Grid>
                                    <HimsAnalysisImage name={component.name} img={component.stateImg} size={330} />
                                </Grid>
                                {component.faultImg && (
                                    <Grid>
                                        <HimsAnalysisImage name={component.name} img={component.faultImg} size={330} />
                                    </Grid>
                                )}
                            </Grid>
                        </Box>
                    ))}
                </>
            )}
        </Paper>
    );
}

function HimsAnalysisImage({ name, img, size }) {
    const [open, setOpen] = useState(false);
    return (
        <>
            <Button onClick={() => setOpen(true)} sx={{ p: 0 }} data-testid="openImage">
                <Box component="img" src={img} sx={{ maxHeight: size, maxWidth: "100%" }} />
            </Button>
            {open && (
                <Dialog open={open} onClose={() => setOpen(false)} maxWidth="lg">
                    <DialogTitle>{name}</DialogTitle>
                    <DialogContent>
                        <img src={img} style={{ maxHeight: 450 }} />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setOpen(false)} sx={{ mr: "auto" }} data-testid="closeImage">
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </>
    );
}
